import {
  InmarDisposition,
  InmarDispositionAction,
  InmarItemIdResolutionSource,
} from "@redotech/redo-return-core-common/integrations/returns/inmar/inmar";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const inmarEnvironmentConfigSchema = z.object({
  consumerId: z.string().nullish(),
  accountIds: z.array(z.string()).nullish(),
  clientId: z.string().nullish(),
  clientSecret: z.string().nullish(),
  webhookSecret: z.string().nullish(),
});

const inmarItemIdResolutionSchema = z.object({
  universalBarcode: createEnum(InmarItemIdResolutionSource).default(
    InmarItemIdResolutionSource.UPC_BARCODE,
  ),
  itemKey: createEnum(InmarItemIdResolutionSource).default(
    InmarItemIdResolutionSource.SKU,
  ),
});

const inmarCapturedResponseConfigSchema = z.object({
  name: z.string(),
  value: z.string(),
  action: createEnum(InmarDispositionAction),
  restock: z.boolean(),
});

const inmarDispositionMappingSchema = z.object({
  disposition: createEnum(InmarDisposition),
  capturedResponses: z.array(inmarCapturedResponseConfigSchema),
  defaultAction: createEnum(InmarDispositionAction),
  defaultRestock: z.boolean(),
});

export const inmarIntegrationSchema = z.object({
  enabled: z.boolean(),
  developmentConfig: inmarEnvironmentConfigSchema,
  productionConfig: inmarEnvironmentConfigSchema,
  isRolledUpUnderRedo: z.boolean().optional().default(true),
  itemIdResolution: inmarItemIdResolutionSchema,
  dispositionMapping: z.array(inmarDispositionMappingSchema),
});

export type InmarIntegration = z.infer<typeof inmarIntegrationSchema>;

export const getInmarIntegration = {
  name: "getInmarIntegration" as const,
  input: z.object({}),
  output: z.object({ inmarIntegration: inmarIntegrationSchema.nullable() }),
};

export const setInmarIntegration = {
  name: "setInmarIntegration" as const,
  input: inmarIntegrationSchema.refine(
    (data) => {
      // If not rolled up under Redo, clientId and clientSecret are required for both environments
      if (!data.isRolledUpUnderRedo) {
        const devValid =
          data.developmentConfig.clientId &&
          data.developmentConfig.clientSecret;
        const prodValid =
          data.productionConfig.clientId && data.productionConfig.clientSecret;
        if (!devValid || !prodValid) {
          return false;
        }
      }

      // Webhook secrets are required for both environments
      const devWebhookValid = data.developmentConfig.webhookSecret;
      const prodWebhookValid = data.productionConfig.webhookSecret;
      if (!devWebhookValid || !prodWebhookValid) {
        return false;
      }

      return true;
    },
    {
      message:
        "Client ID, Client Secret, and Webhook Secret are required for both environments",
      path: ["developmentConfig"],
    },
  ),
  output: z.object({}),
};

export const disconnectInmarIntegration = {
  name: "disconnectInmarIntegration" as const,
  input: z.object({}),
  output: z.object({}),
};
